import {Card, Chips, Segment, Slider} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil, uniq} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {
  FinancialProductCalculation,
  FinancialProductMoney,
  FinancialProductOffer,
  FinancialProductPreferenceCoordinates,
  LeasingTargetGroup,
} from '@dms/api/sales';
import {CalculatorControlHeading} from '@dms/features/financial-products';
import i18n from '@dms/i18n';

import {
  EMPTY_PLACEHOLDER,
  suffixTestId,
  valueOrPlaceholder,
  type Nullish,
  type TestIdProps,
} from 'shared/web';

import type {DisplayedFinancialProductQuote} from 'features/financial-products';

export interface LeasingPreferenceEditorProps extends TestIdProps {
  offer: FinancialProductOffer;
  coordinates: Extract<FinancialProductPreferenceCoordinates, {productType: 'LEASING'}>;
  displayed: DisplayedFinancialProductQuote;
  isDisabled: boolean;
  onChange: (coordinates: FinancialProductPreferenceCoordinates) => void;
}

// Heading size 1 has a line height of eight design-system spacing units.
const MONTHLY_PAYMENT_HEIGHT = 8;
const RESULT_VALUE_SPACING = 3;

function targetGroupLabel(targetGroup: LeasingTargetGroup) {
  return i18n.t(
    targetGroup === 'PRIVATE'
      ? 'page.vehicle.financingAndLeasing.private'
      : 'page.vehicle.financingAndLeasing.commercial'
  );
}

export function LeasingPreferenceEditor(props: LeasingPreferenceEditorProps) {
  const formatNumber = useFormatNumber();
  const calculation = props.displayed.source === 'NONE' ? null : props.displayed.quote.calculation;
  const isLocallyUnavailable =
    props.displayed.source === 'LOCAL' && props.displayed.quote.availability === 'UNAVAILABLE';
  const months = i18n.t('general.labels.months');
  const availableTerms = uniq(
    props.offer.availableLeasingCells.map(({termMonths}) => termMonths)
  ).sort((left, right) => left - right);
  const availableMileages = uniq(
    props.offer.availableLeasingCells
      .filter(({termMonths}) => termMonths === props.coordinates.termMonths)
      .map(({annualMileage}) => annualMileage)
  ).sort((left, right) => left - right);
  const mileageTicks = availableMileages.map((value) => ({
    value,
    label: formatNumber(value) ?? String(value),
  }));

  const update = (next: Partial<LeasingPreferenceEditorProps['coordinates']>) => {
    const coordinates = {...props.coordinates, ...next};
    const isExactVariant = props.offer.availableLeasingCells.some(
      ({termMonths, annualMileage}) =>
        termMonths === coordinates.termMonths && annualMileage === coordinates.annualMileage
    );

    if (
      isExactVariant &&
      props.offer.availableTargetGroups.includes(coordinates.leasingTargetGroup)
    ) {
      props.onChange(coordinates);
    }
  };

  return (
    <Grid columns={[1, 3]} spacing={6}>
      <GridItem span={[1, 2]}>
        <Card variant="inlineGrey" data-testid={suffixTestId('controls', props)}>
          <VStack spacing={6}>
            <Show when={props.offer.availableTargetGroups.length > 1}>
              <HStack justify="space-between" align="center">
                <Heading size={4}>{i18n.t('page.vehicle.financingAndLeasing.targetGroup')}</Heading>
                <Segment<LeasingTargetGroup>
                  label={i18n.t('page.vehicle.financingAndLeasing.targetGroup')}
                  value={props.coordinates.leasingTargetGroup}
                  options={props.offer.availableTargetGroups.map((targetGroup) => ({
                    value: targetGroup,
                    label: targetGroupLabel(targetGroup),
                  }))}
                  isDisabled={props.isDisabled}
                  onChange={(leasingTargetGroup) => update({leasingTargetGroup})}
                  data-testid={suffixTestId('targetGroup', props)}
                />
              </HStack>
            </Show>
            <VStack spacing={4}>
              <HStack justify="space-between" align="center">
                <CalculatorControlHeading
                  title={i18n.t('page.vehicle.financingAndLeasing.term')}
                  tooltip={i18n.t('page.vehicle.financingAndLeasing.hints.term')}
                  testId={suffixTestId('leasingTermHint', props)}
                />
                <HStack spacing={2} align="center">
                  <Text alternative>{props.coordinates.termMonths}</Text>
                  <Text color="secondary">{months}</Text>
                </HStack>
              </HStack>
              <Chips
                options={availableTerms.map((term) => ({
                  value: term.toString(),
                  label: term.toString(),
                }))}
                value={[props.coordinates.termMonths.toString()]}
                isDisabled={props.isDisabled}
                onChange={(values) => {
                  const termMonths = Number(values?.[0]);
                  // Re-clicking the selected chip must not re-emit the unchanged term.
                  if (
                    !availableTerms.includes(termMonths) ||
                    termMonths === props.coordinates.termMonths
                  ) {
                    return;
                  }
                  const mileages = props.offer.availableLeasingCells
                    .filter((variant) => variant.termMonths === termMonths)
                    .map(({annualMileage}) => annualMileage);
                  const annualMileage = mileages.includes(props.coordinates.annualMileage)
                    ? props.coordinates.annualMileage
                    : mileages.at(0);

                  if (!isNil(annualMileage)) {
                    update({termMonths, annualMileage});
                  }
                }}
                data-testid={suffixTestId('leasingTerm', props)}
              />
            </VStack>
            <VStack spacing={4}>
              <HStack justify="space-between" align="center">
                <Heading size={4}>{i18n.t('entity.vehicle.labels.annualMileage')}</Heading>
                <HStack spacing={2} align="center">
                  <Text alternative>{formatNumber(props.coordinates.annualMileage)}</Text>
                  <Text color="secondary">{i18n.t('general.metric.km')}</Text>
                </HStack>
              </HStack>
              <Slider
                min={availableMileages.at(0) ?? 0}
                max={availableMileages.at(-1) ?? 0}
                step={availableMileages}
                ticks={mileageTicks}
                nonlinearTicks={mileageTicks}
                value={props.coordinates.annualMileage}
                displayValue="off"
                isDisabled={props.isDisabled || availableMileages.length < 2}
                onChange={(annualMileage) => {
                  if (availableMileages.includes(annualMileage)) {
                    update({annualMileage});
                  }
                }}
                data-testid={suffixTestId('annualMileage', props)}
              />
            </VStack>
          </VStack>
        </Card>
      </GridItem>
      <GridItem>
        <Box
          backgroundColor="palettes.neutral.900.100"
          borderRadius="medium"
          boxShadow="elevation_3"
          padding={5}
        >
          <VStack spacing={4}>
            <Text size="small" color="tertiary" alternative>
              {`${props.coordinates.termMonths} ${months}`}
            </Text>
            <Show when={isLocallyUnavailable}>
              <Text color="secondary">{i18n.t('financialProducts.unavailable.generic')}</Text>
            </Show>
            <LeasingResult
              calculation={calculation}
              leasingCosts={props.offer.leasingCosts}
              coordinates={props.coordinates}
              data-testid={props['data-testid']}
            />
          </VStack>
        </Box>
      </GridItem>
    </Grid>
  );
}

/**
 * Every slot stays rendered, with placeholders when nothing prices the selection. The services
 * slots depend only on the offer, so they do not appear or vanish while the seller moves controls.
 */
function LeasingResult(
  props: Pick<LeasingPreferenceEditorProps, 'coordinates' | 'data-testid'> & {
    calculation: FinancialProductCalculation | null;
    leasingCosts: FinancialProductOffer['leasingCosts'];
  }
) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const calculation = props.calculation;
  const formatMoneyOrPlaceholder = (money: FinancialProductMoney | Nullish) =>
    isNil(money)
      ? EMPTY_PLACEHOLDER
      : valueOrPlaceholder(formatCurrency(Number(money.amount), money.currency));
  const monthlyPayment = formatMoneyOrPlaceholder(calculation?.monthlyPayment);
  const downPayment = formatMoneyOrPlaceholder(calculation?.leasingDownPayment);
  const hasMonthlyServices = isNotNilOrEmpty(props.leasingCosts?.monthlyServices);
  // The totals are typed non-optional, but DMS does not parse responses: an older backend omits them.
  const leasingTotalForTerm = calculation?.leasingTotalForTerm;
  const totalOverTerm = isNil(calculation)
    ? null
    : (leasingTotalForTerm ??
      calculation.totalPayable ?? {
        amount: Number(calculation.monthlyPayment.amount) * props.coordinates.termMonths,
        currency: calculation.monthlyPayment.currency,
      });
  const leasingDownPayment = calculation?.leasingDownPayment;
  const paidDownPayment =
    isNotNil(leasingDownPayment) && Number(leasingDownPayment.amount) > 0
      ? valueOrPlaceholder(
          formatCurrency(Number(leasingDownPayment.amount), leasingDownPayment.currency)
        )
      : null;
  // Names what the customer's total adds to rate × term; a zero down payment is never named.
  const totalOverTermNote = isNil(leasingTotalForTerm)
    ? null
    : match({hasMonthlyServices, paidDownPayment})
        .with({hasMonthlyServices: true, paidDownPayment: Pattern.string}, ({paidDownPayment}) =>
          i18n.t('page.businessCase.financialProducts.includingServicesAndDownPayment', {
            downPayment: paidDownPayment,
          })
        )
        .with({hasMonthlyServices: true, paidDownPayment: null}, () =>
          i18n.t('page.businessCase.financialProducts.includingServices')
        )
        .with(
          {hasMonthlyServices: false, paidDownPayment: Pattern.string},
          ({paidDownPayment}) =>
            `${i18n.t('page.businessCase.financialProducts.includingDownPayment')} ${paidDownPayment}`
        )
        .with({hasMonthlyServices: false, paidDownPayment: null}, () => null)
        .exhaustive();

  return (
    <>
      <VStack spacing={2}>
        <Text color="tertiary">
          {i18n.t('page.vehicle.financingAndLeasing.resultingMonthlyRate')}
        </Text>
        <HStack justify="flex-end" align="center" height={MONTHLY_PAYMENT_HEIGHT}>
          <Heading
            size={1}
            color="warning"
            align="right"
            data-testid={suffixTestId('monthlyPayment', props)}
          >
            {monthlyPayment}
          </Heading>
        </HStack>
      </VStack>
      <VStack spacing={RESULT_VALUE_SPACING}>
        <Show when={hasMonthlyServices}>
          <Box data-testid={suffixTestId('monthlyServices', props)}>
            <LeasingResultValue
              label={i18n.t('page.vehicle.financingAndLeasing.monthlyServices')}
              value={
                isNil(calculation)
                  ? EMPTY_PLACEHOLDER
                  : formatMoneyOrPlaceholder(props.leasingCosts?.monthlyServicesTotal)
              }
            />
          </Box>
          <Box data-testid={suffixTestId('totalMonthlyPayment', props)}>
            <LeasingResultValue
              label={i18n.t('page.businessCase.financialProducts.totalMonthlyPayment')}
              value={formatMoneyOrPlaceholder(calculation?.leasingTotalMonthlyPayment)}
            />
          </Box>
        </Show>
        <LeasingResultValue
          label={i18n.t('page.vehicle.financingAndLeasing.targetGroup')}
          value={targetGroupLabel(props.coordinates.leasingTargetGroup)}
        />
        <LeasingResultValue
          label={i18n.t('entity.vehicle.labels.annualMileage')}
          value={valueOrPlaceholder(formatNumber(props.coordinates.annualMileage))}
        />
        <Box data-testid={suffixTestId('totalOverTerm', props)}>
          <LeasingResultValue
            label={i18n.t('page.vehicle.financingAndLeasing.totalOverTerm')}
            value={
              isNil(totalOverTerm)
                ? EMPTY_PLACEHOLDER
                : valueOrPlaceholder(
                    formatCurrency(Number(totalOverTerm.amount), totalOverTerm.currency)
                  )
            }
          />
          <Show when={isNotNilOrEmpty(totalOverTermNote) ? totalOverTermNote : null}>
            {(note) => (
              <Text
                size="xSmall"
                color="tertiary"
                data-testid={suffixTestId('totalOverTermNote', props)}
              >
                {note}
              </Text>
            )}
          </Show>
        </Box>
        <Show when={isNotNil(calculation?.leasingDownPayment)}>
          <LeasingResultValue
            label={i18n.t('page.vehicle.financingAndLeasing.downPayment')}
            value={downPayment}
          />
        </Show>
      </VStack>
    </>
  );
}

/**
 * The value sits below its label, never beside it: beside it, a longer value (Commercial instead
 * of Private, a bigger total) shares the line with the label and wraps it in a narrow box, so the
 * box height would follow the selection. Below it, only the label can wrap, and the label is the
 * same in every state.
 */
function LeasingResultValue(props: {label: string; value: string}) {
  return (
    <VStack>
      <Text size="small" color="tertiary">
        {props.label}
      </Text>
      <Text size="small" color="white" alternative>
        {props.value}
      </Text>
    </VStack>
  );
}
